import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Col, Popconfirm, Row, Table, Select, TablePaginationConfig } from 'antd';
import { UnitCategoriesApi, ProjectsApi } from 'api/api';
import { UnitCategoryDto, ProjectDto } from 'api/models';
import { Button } from 'components/buttons/buttons';
import { Cards } from 'components/cards/frame/cards-frame';
import { PageHeader } from 'components/page-headers/page-headers';
import { CardToolbox, Main, TableWrapper, TopToolBox } from 'container/styled';
import { useTranslation } from 'react-i18next';
import { hasPermission } from 'utility/accessibility/hasPermission';
import FeatherIcon from 'feather-icons-react';
import openNotificationWithIcon from 'utility/notification';
import { ExportButtonPageHeader } from 'components/buttons/export-button/export-button';
import { Link } from 'react-router-dom';
import { CustomTableStyleWrapper } from 'pages/style';
import { UnitCategoryForm } from './UnitCategoryForm';
import { AutoComplete } from 'components/autoComplete/autoComplete';
import { filterKeys } from 'constants/constants';

const { Option } = Select;
const categoriesApi = new UnitCategoriesApi();
const projectsApi = new ProjectsApi();

type ModalState = {
  visible: boolean;
  editMode: boolean;
  selectedCategory: UnitCategoryDto | null;
};

export const UnitCategoriesPage: React.FC = () => {
  const { t } = useTranslation();
  const searchTimeout = useRef<NodeJS.Timeout | null>(null);

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<UnitCategoryDto[]>([]);
  const [modalState, setModalState] = useState<ModalState>({
    visible: false,
    editMode: false,
    selectedCategory: null,
  });
  const [projects, setProjects] = useState<ProjectDto[]>([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [query, setQuery] = useState({
    searchTerm: '',
    pageNumber: 1,
    pageSize: 10,
    isActive: undefined as boolean | undefined,
    projectId: undefined as number | undefined,
    filterType: -1,
  });
  const [pagination, setPagination] = useState<TablePaginationConfig>({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const fetchCategories = useCallback(async () => {
    try {
      setLoading(true);
      const response = await categoriesApi.apiUnitCategoriesGet(query);
      setData(response.data.items || []);
      setPagination({
        ...pagination,
        current: query.pageNumber,
        total: response.data.totalCount,
      });
    } catch (err) {
      openNotificationWithIcon('error', t('categories:fetch-error', 'Failed to fetch categories'));
    } finally {
      setLoading(false);
    }
  }, [query, t]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        setProjectsLoading(true);
        const response = await projectsApi.apiProjectsGet({ pageSize: -1, isActive: true });
        setProjects(response.data.items || []);
      } catch (err) {
        openNotificationWithIcon('error', t('projects:fetch-error', 'Failed to fetch projects'));
      } finally {
        setProjectsLoading(false);
      }
    };

    fetchProjects();
  }, [t]);

  const columns = [
    {
      title: t('categories:name', 'Name'),
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: t('categories:project', 'Project'),
      dataIndex: 'projectName',
      key: 'projectName',
    },
    {
      title: t('categories:description', 'Description'),
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: t('categories:floors', 'Floors'),
      dataIndex: 'floorsCount',
      key: 'floorsCount',
      render: (floorsCount: number) => floorsCount || t('categories:no-floors', 'No floors'),
    },
    {
      title: t('categories:available-floors', 'Available Floors'),
      dataIndex: 'availableFloorsCount',
      key: 'availableFloorsCount',
      render: (availableFloorsCount: number, record: UnitCategoryDto) =>
        record.floorsCount ? availableFloorsCount : '-',
    },
    {
      title: t('categories:units', 'Units'),
      dataIndex: 'unitsCount',
      key: 'unitsCount',
    },
    {
      title: t('categories:available-unit-stock', 'Available Unit Stock'),
      dataIndex: 'availableUnitsCount',
      key: 'availableUnitsCount',
    },
    {
      title: t('categories:status', 'Status'),
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive: boolean) => (
        <span className={`status-text ${isActive ? 'active' : 'deactivate'}`}>
          {isActive ? t('global:active', 'Active') : t('global:inactive', 'Inactive')}
        </span>
      ),
    },
    {
      title: t('global:actions', 'Actions'),
      key: 'action',
      width: 150,
      render: (record: UnitCategoryDto) => (
        <div className="table-actions">
          {hasPermission('unit-categories:view') && (
            <Link to={`/unit-categories/${record.id}`}>
              <Button className="btn-icon" type="default" shape="circle">
                <FeatherIcon icon="eye" size={16} />
              </Button>
            </Link>
          )}
          {hasPermission('unit-categories:edit') && (
            <Button className="btn-icon" type="primary" shape="circle" onClick={() => handleEdit(record)}>
              <FeatherIcon icon="edit" size={16} />
            </Button>
          )}
          {hasPermission('unit-categories:delete') && (
            <Popconfirm
              title={t('categories:delete-confirm', 'Are you sure you want to delete this category?')}
              onConfirm={() => handleDelete(record.id!)}
              okText={t('global:yes', 'Yes')}
              cancelText={t('global:no', 'No')}
            >
              <Button className="btn-icon" type="danger" shape="circle">
                <FeatherIcon icon="trash-2" size={16} />
              </Button>
            </Popconfirm>
          )}
        </div>
      ),
    },
  ];

  const handleSearch = (value: string) => {
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }
    searchTimeout.current = setTimeout(() => {
      setQuery(prev => ({ ...prev, searchTerm: value, pageNumber: 1 }));
    }, 300);
  };

  const handleModalSubmit = async (): Promise<void> => {
    resetModalState();
    await fetchCategories();
  };

  const resetModalState = () => {
    setModalState({
      visible: false,
      editMode: false,
      selectedCategory: null,
    });
  };

  const handleEdit = (category: UnitCategoryDto) => {
    setModalState({
      visible: true,
      editMode: true,
      selectedCategory: category,
    });
  };

  const handleDelete = async (id: number) => {
    try {
      await categoriesApi.apiUnitCategoriesIdDelete({ id });
      openNotificationWithIcon('success', t('categories:deleted', 'Category deleted successfully'));
      fetchCategories();
    } catch (err) {
      openNotificationWithIcon('error', t('categories:delete-error', 'Failed to delete category'));
    }
  };

  const handleStatusFilterChange = (value: number) => {
    setQuery(prev => ({
      ...prev,
      filterType: value,
      isActive: value === -1 ? undefined : value === 1,
      pageNumber: 1,
      pageSize: 10,
    }));
  };

  const handleProjectFilterChange = (value: number) => {
    setQuery(prev => ({
      ...prev,
      projectId: value === -1 ? undefined : value,
      pageNumber: 1,
    }));
  };

  return (
    <>
      <CardToolbox>
        <PageHeader
          ghost
          title={t('categories:title', 'Unit Categories')}
          buttons={[
            <ExportButtonPageHeader
              key="1"
              title={t('categories:title', 'Categories')}
              data={data}
              columns={['name', 'projectName', 'unitsCount', 'availableUnitsCount', 'isActive']}
            />,
            hasPermission('unit-categories:create') && (
              <Button
                key="2"
                type="primary"
                onClick={() => setModalState({ visible: true, editMode: false, selectedCategory: null })}
              >
                <FeatherIcon icon="plus" size={16} />
                {t('categories:create', 'Add Category')}
              </Button>
            ),
          ]}
        />
      </CardToolbox>

      <Main>
        <Cards headless>
          <Row gutter={15}>
            <Col xs={24}>
              <TopToolBox>
                <Row gutter={15}>
                  <Col lg={6} md={6} xs={24}>
                    <div className="table-search-box">
                      <AutoComplete
                        onSearch={handleSearch}
                        width="100%"
                        patterns
                        placeholder={t('categories:search', 'Search categories...')}
                      />
                    </div>
                  </Col>

                  <Col lg={12} md={14} xs={24}>
                    <div style={{ display: 'flex', gap: '20px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span>{t('projects:project-filter', 'Project:')}</span>
                        <Select
                          style={{ width: 200 }}
                          onChange={handleProjectFilterChange}
                          value={query.projectId ?? -1}
                          loading={projectsLoading}
                        >
                          <Select.Option value={-1}>{t('global:all', 'All')}</Select.Option>
                          {projects.map(project => (
                            <Select.Option key={project.id} value={project.id}>
                              {project.name}
                            </Select.Option>
                          ))}
                        </Select>
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span>{t('users:lable.status', 'Status:')}</span>
                        <Select style={{ width: 200 }} onChange={handleStatusFilterChange} value={query.filterType}>
                          {filterKeys.map(item => (
                            <Select.Option key={item.id} value={item.id}>
                              {t(item.key, item.name)}
                            </Select.Option>
                          ))}
                        </Select>
                      </div>
                    </div>
                  </Col>
                </Row>
              </TopToolBox>
            </Col>
          </Row>
          <CustomTableStyleWrapper>
            <TableWrapper>
              <Table
                loading={loading}
                dataSource={data}
                columns={columns}
                pagination={{
                  current: query.pageNumber,
                  pageSize: query.pageSize,
                  total: pagination.total,
                  showSizeChanger: true,
                  pageSizeOptions: [10, 50, 100, 1000],
                  showTotal: (total, range) =>
                    `${range[0]}-${range[1]} ${t('global.of', 'of')} ${total} ${t('global.items', 'items')}`,
                  onChange: (pageNumber, pageSize) => setQuery(prev => ({ ...prev, pageNumber, pageSize })),
                }}
              />
            </TableWrapper>
          </CustomTableStyleWrapper>
        </Cards>
      </Main>

      <UnitCategoryForm
        visible={modalState.visible}
        editMode={modalState.editMode}
        category={modalState.selectedCategory}
        onCancel={() => resetModalState()}
        onSuccess={handleModalSubmit}
      />
    </>
  );
};
